/**
 * Profile-blob cipher — AES-GCM over a JSON envelope containing the
 * user's display fields (name, avatarHash, about, emojiStatus).
 *
 * The blob lives on the server as opaque ciphertext keyed by userId;
 * only holders of the user's ProfileKey can decrypt. The encryption key
 * is derived from the raw ProfileKey via HKDF with a feature-specific
 * info string so the same 32-byte secret cannot be confused across
 * domains (e.g., a future "private notes" feature reusing the key
 * material would derive a different subkey and produce a different
 * ciphertext that this cipher refuses to open).
 *
 * AES-GCM additional-authenticated-data binds the ciphertext to this
 * domain: a leaked blob from a different feature that happened to use
 * the same profile key cannot be substituted in, because its AAD would
 * differ. The IV/nonce is randomised on every seal — two consecutive
 * seal() calls of the same plaintext produce different ciphertext.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ProfileBlob.swift` byte-for-byte.
 *
 * HKDF info : "kaether:profile:enc:v1"
 * AES-GCM AAD: "kaether:profile:v1"
 */

import { ProfileKey, ProfileKeyError } from "./ProfileKey";

// ---------------------------------------------------------------------------
// ProfileBlob value type
// ---------------------------------------------------------------------------

export interface ProfileBlobData {
  readonly schema: number;
  readonly name?: string | null;
  /** Base64-encoded SHA-256 of raw avatar bytes (optional). */
  readonly avatarHash?: string | null;
  readonly about?: string | null;
  readonly emojiStatus?: string | null;
  /** Unix seconds; null/undefined = no expiry. */
  readonly statusExpiresAt?: number | null;
}

export const PROFILE_BLOB_CURRENT_SCHEMA = 1;

export function makeProfileBlob(fields: {
  name?: string | null;
  avatarHash?: string | null;
  about?: string | null;
  emojiStatus?: string | null;
  statusExpiresAt?: number | null;
}): ProfileBlobData {
  return {
    schema: PROFILE_BLOB_CURRENT_SCHEMA,
    name: fields.name ?? null,
    avatarHash: fields.avatarHash ?? null,
    about: fields.about ?? null,
    emojiStatus: fields.emojiStatus ?? null,
    statusExpiresAt: fields.statusExpiresAt ?? null,
  };
}

// ---------------------------------------------------------------------------
// HKDF + AES-GCM constants (CRITICAL — must match iOS byte-for-byte)
// ---------------------------------------------------------------------------

/** HKDF info string: "kaether:profile:enc:v1" */
export const PROFILE_BLOB_HKDF_INFO = "kaether:profile:enc:v1";

/** AES-GCM AAD string: "kaether:profile:v1" */
export const PROFILE_BLOB_AAD = "kaether:profile:v1";

// ---------------------------------------------------------------------------
// ProfileBlobCipher
// ---------------------------------------------------------------------------

/**
 * Pure-crypto helpers for sealing and opening profile blobs.
 * Uses SubtleCrypto exclusively; no platform-specific deps.
 */
export const ProfileBlobCipher = {
  /**
   * HKDF-SHA256(profileKey, salt=zeros32, info="kaether:profile:enc:v1")
   * → 32-byte AES-GCM key. Deterministic from the same input key.
   *
   * Matches iOS:
   *   HKDF<SHA256>.deriveKey(inputKeyMaterial: SymmetricKey(data: pk.raw),
   *                          salt: Data(repeating: 0, count: 32),
   *                          info: Data("kaether:profile:enc:v1".utf8),
   *                          outputByteCount: 32)
   */
  async deriveBlobKey(pk: ProfileKey): Promise<CryptoKey> {
    const ikm = await crypto.subtle.importKey(
      "raw",
      pk.raw,
      { name: "HKDF" },
      false,
      ["deriveKey"],
    );

    const salt = new Uint8Array(32); // 32 × 0x00
    const info = new TextEncoder().encode(PROFILE_BLOB_HKDF_INFO);

    return crypto.subtle.deriveKey(
      { name: "HKDF", hash: "SHA-256", salt, info },
      ikm,
      { name: "AES-GCM", length: 256 },
      // extractable: false — mirrors iOS non-exportable SymmetricKey
      false,
      ["encrypt", "decrypt"],
    );
  },

  /**
   * Encrypts a profile blob. The 12-byte nonce is random per call; the AAD
   * binds the ciphertext to the profile-blob domain.
   *
   * Wire format: nonce(12) ‖ ciphertext ‖ tag(16)
   * — identical to CryptoKit's AES.GCM.SealedBox.combined layout.
   */
  async seal(blob: ProfileBlobData, key: ProfileKey): Promise<Uint8Array> {
    const subkey = await ProfileBlobCipher.deriveBlobKey(key);

    let plaintext: Uint8Array;
    try {
      plaintext = new TextEncoder().encode(JSON.stringify(blob));
    } catch {
      throw new ProfileKeyError("sealFailure", "JSON encode failed");
    }

    const nonce = new Uint8Array(12);
    crypto.getRandomValues(nonce);

    const aad = new TextEncoder().encode(PROFILE_BLOB_AAD);

    let ciphertextWithTag: ArrayBuffer;
    try {
      ciphertextWithTag = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv: nonce, additionalData: aad },
        subkey,
        plaintext,
      );
    } catch {
      throw new ProfileKeyError("sealFailure", "AES-GCM encrypt failed");
    }

    // combined = nonce(12) ‖ ciphertext ‖ tag(16)
    const combined = new Uint8Array(12 + ciphertextWithTag.byteLength);
    combined.set(nonce, 0);
    combined.set(new Uint8Array(ciphertextWithTag), 12);
    return combined;
  },

  /**
   * Decrypts a profile blob. AAD mismatch or wrong key surfaces as
   * "openFailure" — never a partial decrypt.
   */
  async open(encryptedBlob: Uint8Array, key: ProfileKey): Promise<ProfileBlobData> {
    if (encryptedBlob.byteLength < 12 + 16) {
      throw new ProfileKeyError("openFailure", "ciphertext too short");
    }

    const subkey = await ProfileBlobCipher.deriveBlobKey(key);
    const nonce = encryptedBlob.slice(0, 12);
    const ciphertextWithTag = encryptedBlob.slice(12);
    const aad = new TextEncoder().encode(PROFILE_BLOB_AAD);

    let plaintext: ArrayBuffer;
    try {
      plaintext = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: nonce, additionalData: aad },
        subkey,
        ciphertextWithTag,
      );
    } catch {
      throw new ProfileKeyError("openFailure", "AES-GCM decrypt failed");
    }

    let blob: ProfileBlobData;
    try {
      blob = JSON.parse(new TextDecoder().decode(plaintext)) as ProfileBlobData;
    } catch {
      throw new ProfileKeyError("malformedBlob", "JSON decode failed");
    }

    if (blob.schema !== PROFILE_BLOB_CURRENT_SCHEMA) {
      throw new ProfileKeyError(
        "unsupportedSchema",
        `unsupported schema: ${blob.schema}`,
      );
    }

    return blob;
  },
};
